import { Box, Card, CardContent, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import React, { useState } from "react";
import CardTitle from "./CardTitle";
import MiniField from "./MiniField";
import { useDispatch, useSelector } from "react-redux";
import { updateDataFilterSetting } from "../../reducer/CalcSettingsSlice";
import { DataFilterTypes, capitalizeFirstLetter, roundToX } from "../../common/common";
import { numberSx, tokens } from "../../theme";

export default function FocusRemoveWorst(props) {
    const calcSettings = useSelector(state => state.calcSettings);
    const laserData = useSelector(state => state.laserData);
    const focusSetting = calcSettings.changes.dataFilters.find(df => df.type === DataFilterTypes.FocusRemoveWorst);
    const [minPointError, setMinPointError] = useState(false);
    const [iterationError, setIterationError] = useState(false);
    const [keepBestError, setKeepBestError] = useState(false);
    const dispatch = useDispatch();

    const handleEliminatePointChange = (evt) => {
        if (evt.target.value.match(/^[0-9]*$/)) {
            evt.preventDefault();
            let error = (evt.target.value.length === 0) || (Number(evt.target.value) ===    0);
            setMinPointError(error);
            dispatch(updateDataFilterSetting({
                type: DataFilterTypes.FocusRemoveWorst,
                property: "EliminatePercent",
                value: String(evt.target.value / 100),
                error: error
            }));
        }
    }

    const handleKeepBestChange = (evt) => {
        if (evt.target.value.match(/^[0-9]*$/)) {
            evt.preventDefault();
            let error = (evt.target.value.length === 0) || (Number(evt.target.value) ===    0);
            setKeepBestError(error);
            dispatch(updateDataFilterSetting({
                type: DataFilterTypes.FocusRemoveWorst,
                property: "KeepBestPercent",
                value: String(evt.target.value / 100),
                error: error
            }));
        }
    }

    const handleIterationsChange = (evt) => {
        if (evt.target.value.match(/^[0-9]*$/)) {
            evt.preventDefault();
            let error = (evt.target.value.length === 0) || (Number(evt.target.value) ===    0);
            setIterationError(error);
            dispatch(updateDataFilterSetting({
                type: DataFilterTypes.FocusRemoveWorst,
                property: "Iterations",
                value: evt.target.value,
                error: error
            }));
        }
    }

    const calcPercent = (zRemoved, yRemoved, initialPointCount) => {
        return `${roundToX(((zRemoved + yRemoved)/initialPointCount) * 100,1)}%`;
    }

    // One compact table with Machine/Motor as rows, per the mockup - not the two separate per-shaft
    // mini-tables it replaced.
    //
    // Initial and the removed count are shown despite the mockup omitting both (Kevin, 2026-08-17):
    // a percentage alone cannot be checked against anything. Knowing 155 of 309 went is what makes
    // 50.2% meaningful, and the initial count is also how you see that the two shafts started with
    // very different amounts of data.
    // Headers are single words and never wrap: "Y removed"/"Z removed" wrapped onto two lines each
    // and pushed the table into a horizontal scroll at rail width. The shared meaning is carried by
    // the "Removed" group header spanning them instead, which is how the pre-redesign card read.
    const headerCellSx = {
        fontSize: "10.5px", fontWeight: 600, color: tokens.muted,
        textTransform: "uppercase", letterSpacing: "0.04em", border: "none", padding: "3px 4px",
        whiteSpace: "nowrap",
    };
    const groupHeaderCellSx = {
        ...headerCellSx,
        borderBottom: `1px solid ${tokens.line}`,
    };
    const rowHeaderCellSx = {
        fontSize: "12px", fontWeight: 600, color: tokens.textSoft, border: "none", padding: "3px 4px",
        whiteSpace: "nowrap",
    };
    const numCellSx = { ...numberSx, fontSize: "12px", border: "none", padding: "3px 4px" };

    const renderTraceTable = () => {
        const rows = ["machine", "motor"]
            .map((dataType) => ({
                dataType,
                filter: laserData.filterTrace.filters.find(f => f.type === "FocusRemoveWorst" && f.data === dataType),
            }))
            .filter((row) => row.filter);

        if (rows.length === 0) {
            return <></>;
        }

        return (
            <TableContainer>
                <Table size="small" sx={{ mt: 1 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellSx} />
                            <TableCell sx={headerCellSx} />
                            <TableCell align="center" colSpan={4} sx={groupHeaderCellSx}>Removed</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={headerCellSx} />
                            <TableCell align="right" sx={headerCellSx}>Initial</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Y</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Z</TableCell>
                            <TableCell align="right" sx={headerCellSx}>Total</TableCell>
                            <TableCell align="right" sx={headerCellSx}>%</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(({ dataType, filter }) => (
                            <TableRow key={dataType}>
                                <TableCell component="th" scope="row" sx={rowHeaderCellSx}>
                                    {capitalizeFirstLetter(dataType)}
                                </TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.initialpointcount}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.removeY.removed}</TableCell>
                                <TableCell align="right" sx={numCellSx}>{filter.trace.removeZ.removed}</TableCell>
                                <TableCell align="right" sx={numCellSx}>
                                    {filter.trace.removeY.removed + filter.trace.removeZ.removed}
                                </TableCell>
                                <TableCell align="right" sx={numCellSx}>
                                    {calcPercent(filter.trace.removeZ.removed, filter.trace.removeY.removed, filter.trace.initialpointcount)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    const renderAttributes = () => {
        if(focusSetting) {
            return(
                <Stack>
                    <Box sx={{ display: "flex", gap: "8px", mb: "10px" }}>
                        <MiniField
                            label="Iterations"
                            value={focusSetting.attributes.Iterations}
                            onChange={handleIterationsChange}
                            maxLength={3}
                            error={iterationError}
                            disabled={!focusSetting.enabled}
                        />
                        <MiniField
                            label="Eliminate worst %"
                            value={roundToX(focusSetting.attributes.EliminatePercent * 100, 0)}
                            onChange={handleEliminatePointChange}
                            maxLength={2}
                            error={minPointError}
                            disabled={!focusSetting.enabled}
                        />
                        <MiniField
                            label="Keep best %"
                            value={(isNaN(focusSetting.attributes.KeepBestPercent)) ? 80 : roundToX(focusSetting.attributes.KeepBestPercent * 100, 0)}
                            onChange={handleKeepBestChange}
                            maxLength={2}
                            error={keepBestError}
                            disabled={!focusSetting.enabled}
                        />
                    </Box>
                    {renderTraceTable()}
                </Stack>
            );
        } else {
            return ("no settings");
        }
    }

    return(
        <Card variant="outlined" >
            <CardContent >
                <CardTitle title="Focus - Remove Worst x%" />
                {renderAttributes()}
            </CardContent>
        </Card>
    )
}